// What the opponent is thinking, as text a person can read.
//
// A developer view, and the reason it exists is that the planning layer is the
// only part of this game with **no visible output**. A belt shows its cargo, a
// foundry shows its recipe, a march draws itself on the board — and the brief,
// which is now the thing that decides every turn, is a structure that lives
// for one turn inside a worker and is never seen by anybody.
//
// That has already cost real time. "The opponent barely mints" was an
// instrument fault that survived several rounds of being reasoned about; the
// dig flows were correct and the harvester still would not move; the warm-up
// batch silently ran at full size. Every one of those was a number nobody was
// looking at, and every one was found the moment somebody printed it.
//
// Pure, like `pieceBadges` and `marchView`: this returns lines, and the
// component draws them. It is testable that way, and it cannot accidentally
// reach into the renderer.

import { fileOf, rankOf } from "src/engine/rules/board";
import { squareLabel } from "src/engine/session/gridLabels";
import type { GameState } from "src/engine/rules/commands";
import type { Brief, Flow } from "src/engine/ai/flows";

/** One row of the planning view. */
export interface PlanningRow {
  /** What this row is about, for grouping and colour. */
  kind: "summary" | "plan" | "proposal" | "note";
  text: string;
}

const at = (square: number): string => squareLabel(square);

const away = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * Describe one flow: what is being moved, where, how much a square is worth,
 * and — the part only a plan has — how long it has been under way.
 */
function describe(flow: Flow): PlanningRow {
  const distance = away(flow.subject, flow.toward);
  const age = flow.kind === "plan" ? ` · ${flow.age} turn${flow.age === 1 ? "" : "s"} in` : "";
  return {
    kind: flow.kind,
    text:
      `${at(flow.subject)} → ${at(flow.toward)}  ${distance} away  ` +
      `carrying ${Math.round(flow.value)}  ${flow.worth.toFixed(1)}/sq${age}`
  };
}

/**
 * The whole brief as readable lines, worst-first by what it is worth.
 *
 * Sorted by `worth` rather than by square because the question a person asks
 * of this view is "what is driving the turn", and the answer is whichever flow
 * is paying most per square. Square order would be a list; this is a ranking.
 */
export function planningRows(state: GameState, brief: Brief): PlanningRow[] {
  const rows: PlanningRow[] = [];

  const plans = brief.flows.filter((flow) => flow.kind === "plan").length;
  const total = brief.byKind.plan + brief.byKind.proposal;

  rows.push({
    kind: "summary",
    text:
      `${brief.flows.length} flow${brief.flows.length === 1 ? "" : "s"}` +
      ` · ${plans} under way · ${brief.flows.length - plans} proposed`
  });

  if (brief.flows.length > 0) {
    // The share each kind is contributing. A plan layer quietly contributing
    // nothing looks exactly like one that is working, and this is the only
    // place that difference is visible.
    const share = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));
    rows.push({
      kind: "summary",
      text: `plans ${share(brief.byKind.plan)}% · proposals ${share(brief.byKind.proposal)}% of the pull`
    });
  }

  for (const flow of [...brief.flows].sort((a, b) => b.worth - a.worth)) {
    rows.push(describe(flow));
  }

  if (brief.flows.length === 0) {
    // Said explicitly, because an empty list and a broken brief look the same.
    rows.push({
      kind: "note",
      text: "nothing is carrying material toward a machine that would take it"
    });
  }

  for (const warning of brief.warnings) rows.push({ kind: "note", text: warning });

  // Whose turn it actually is, because this view is *about* the other side and
  // reading it against the wrong army is an easy mistake to make silently.
  rows.push({ kind: "note", text: `side ${state.position.turn} to move` });

  return rows;
}
